import React, { Component } from "react";
import { post } from "aws-amplify/api";
import { FormGroup, FormControl, HelpBlock, Panel, ListGroup, ListGroupItem, InputGroup, Glyphicon } from "react-bootstrap";
import LoaderButton from "../components/LoaderButton";
import ls from 'local-storage';
import Header from "../components/Header";
import "./NewNote.css";

export default class NewNote extends Component {
  constructor(props) {
    super(props);

    const location = Object.values(ls.get("location")).find(obj => obj.place_id === ls.get("location_id"));

    this.state = {
      isLoading: null,
      isDrafting: null,
      isRefining: null,
      title: "", 
      description: "", 
      prompt: "",
      evidenceUrl: "",
      evidence: [],
      location: location.name,
      place_id: ls.get("location_id"),
      rateLimit: 0,
      draftError: ""
    };

  }

  createNote(note) {
    return post({
      apiName: "notes",
      path: "/notes",
      options: {
        body: note
      }
    });
  }

  draftDescription(draft) {
    return post({
      apiName: "notes",
      path: "/notes/draft",
      options: {
        body: draft
      }
    });
  }

  refineDescription(draft) {
    return post({
      apiName: "notes",
      path: "/notes/draft/refine",
      options: {
        body: draft
      }
    });
  }

  async responseBody(op) {
    const { body } = await op.response;
    return await body.json();
  }

  validateForm() {
    return this.state.location.length > 0 && 
    this.state.title.length > 0 && 
    this.state.description.length > 0; 
  }

  validateEvidenceForm() {
    return this.state.evidenceUrl.length > 0;
  }

  validateDraftForm() {
    return this.state.prompt.trim().length > 0;
  }

  validateRefineForm() {
    return this.state.prompt.trim().length > 0;
  }

  handleChange = event => {
    this.setState({
      [event.target.id]: event.target.value
    });
  }

  handleSubmit = async event => {
    event.preventDefault();

    this.setState({ isLoading: true });

    try {
      await this.createNote({
        title: this.state.title,
        description: this.state.description,
        evidence: this.state.evidence.map(item => ({ url: item.url })),
        location: this.state.location,
        place_id: this.state.place_id
      });
      this.props.history.push("/");
    } catch (e) {
      // TODO: Do something better here.
      console.log(e.response);
      this.setState({ isLoading: false,
                      rateLimit: Math.ceil(e.response.data.rateLimit) 
                    });
    }
  }

  handleEvidenceSubmit = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState(prevState => ({
      evidence: [
        ...prevState.evidence,
        {
          evidence_id: `new-${Date.now()}`,
          url: prevState.evidenceUrl
        }
      ],
      evidenceUrl: ""
    }));
  }

  handleDraftSubmit = async event => {
    if (event) {
      event.preventDefault();
    }

    this.setState({
      isDrafting: true,
      draftError: ""
    });

    try {
      const op = await this.draftDescription({
        title: this.state.title,
        prompt: this.state.prompt,
        evidence: this.state.evidence.map(item => ({ url: item.url })),
        location: this.state.location,
        place_id: this.state.place_id
      });
      const draft = await this.responseBody(op);

      this.setState({
        isDrafting: false,
        prompt: "",
        title: draft.title || this.state.title,
        description: draft.description || ""
      });
    } catch (e) {
      this.setState({
        isDrafting: false,
        draftError: "We could not draft the issue from that evidence. Please try again."
      });
    }
  }

  handleRefineSubmit = async event => {
    if (event) {
      event.preventDefault();
    }

    this.setState({
      isRefining: true,
      draftError: ""
    });

    try {
      const op = await this.refineDescription({
        title: this.state.title,
        prompt: this.state.prompt,
        description: this.state.description,
        evidence: this.state.evidence.map(item => ({ url: item.url })),
        location: this.state.location,
        place_id: this.state.place_id
      });
      const draft = await this.responseBody(op);

      this.setState({
        isRefining: false,
        prompt: "",
        title: draft.title || this.state.title,
        description: draft.description || ""
      });
    } catch (e) {
      this.setState({
        isRefining: false,
        draftError: "We could not update the draft. Please try again."
      });
    }
  }

  handleEvidenceKeyPress = event => {
    if (event.key === "Enter") {
      this.handleEvidenceSubmit(event);
    }
  }

  handlePromptKeyPress = event => {
    if (event.key === "Enter") {
      if (this.hasDraft()) {
        this.handleRefineSubmit(event);
      } else {
        this.handleDraftSubmit(event);
      }
    }
  }

  hasDraft() {
    return this.state.title.length > 0 || this.state.description.length > 0;
  }

  renderPrompt() {
    const hasDraft = this.hasDraft();

    return (
      <FormGroup controlId="prompt">
        <FormControl
          onChange={this.handleChange}
          className="NewNotePrompt"
          value={this.state.prompt}
          placeholder={hasDraft ? "Ask for changes to the draft" : "What should this issue be about?"}
          componentClass="textarea"
        />
      </FormGroup>
    );
  }

  renderDraftButton() {
    const hasDraft = this.hasDraft();

    return (
      <LoaderButton
        block
        bsStyle="success"
        bsSize="large"
        className="NewNoteDraftButton"
        type="button"
        disabled={hasDraft ? !this.validateRefineForm() : !this.validateDraftForm()}
        onClick={hasDraft ? this.handleRefineSubmit : this.handleDraftSubmit}
        isLoading={hasDraft ? this.state.isRefining : this.state.isDrafting}
        text={hasDraft ? "Refine" : "Draft"}
        loadingText={hasDraft ? "Refining..." : "Drafting..."}
      />
    );
  }

  renderCreateGuide() {
    if (this.state.title.length > 0 || this.state.description.length > 0) {
      return null;
    }

    return (
      <div className="NewNoteGuide">
        <div className="NewNoteGuideTitle">How to create an issue</div>
        <div className="NewNoteGuideStep">
          <span className="NewNoteGuideNumber">1</span>
          <span>Say what you want your issue to be about.</span>
        </div>
        <div className="NewNoteGuideStep">
          <span className="NewNoteGuideNumber">2</span>
          <span>Add evidence if you have it.</span>
        </div>
      </div>
    );
  }

  evidenceDomain(url) {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch (e) {
      return url;
    }
  }

  renderDraftCard() {
    return (
        <Panel className="NewNoteDraftCard">
        <Panel.Heading>
          <b>Draft issue</b>
        </Panel.Heading>
        <Panel.Body className="NewNoteDraftIntroBody">
          {this.renderPrompt()}
          <div className="EvidenceDraftHeading">
            <b>Evidence <span className="EvidenceDraftOptional">(optional)</span></b>
          </div>
        </Panel.Body>
        {this.state.evidence.length > 0 &&
          <ListGroup fill className="EvidenceDraftList">
            {this.state.evidence.map(item => (
              <ListGroupItem className="EvidenceDraftItem" key={item.evidence_id}>
                <Glyphicon glyph="link" />
                <span className="EvidenceDraftText">
                  <span className="EvidenceDraftDomain">{this.evidenceDomain(item.url)}</span>
                  <span className="EvidenceDraftUrl">{item.url}</span>
                </span>
              </ListGroupItem>
            ))}
          </ListGroup>
        }
        <Panel.Body className="EvidenceDraftUrlBody">
          <FormGroup controlId="evidenceUrl">
            <InputGroup>
              <FormControl
                onChange={this.handleChange}
                onKeyPress={this.handleEvidenceKeyPress}
                value={this.state.evidenceUrl}
                placeholder="Paste an evidence link"
                type="url"
              />
              <InputGroup.Button>
                <LoaderButton
                  bsStyle="primary"
                  type="button"
                  disabled={!this.validateEvidenceForm()}
                  onClick={this.handleEvidenceSubmit}
                  text="Add"
                  loadingText="Adding..."
                />
              </InputGroup.Button>
            </InputGroup>
          </FormGroup>
        </Panel.Body>
      </Panel>
    );
  }

  render() {
    const hasDraft = this.hasDraft();

    return (
      <div className="NewNote">
      <Header location={this.state.location} disabled={true}/>
        <form onSubmit={this.handleSubmit}>
        <div style={{paddingTop: "10px"}} >
            {this.renderCreateGuide()}

            {hasDraft &&
            <React.Fragment>
              <FormGroup controlId="title">
                <FormControl
                  onChange={this.handleChange}
                  value={this.state.title}
                  placeholder="Drafted title"
                  type="text"
                />
              </FormGroup>
              <FormGroup controlId="description">
                <FormControl
                  onChange={this.handleChange}
                  value={this.state.description}
                  placeholder="Drafted description"
                  componentClass="textarea"
                />
              </FormGroup>
            </React.Fragment>
            }

            {this.renderDraftCard()}

            {this.state.draftError.length > 0 &&
              <HelpBlock>{this.state.draftError}</HelpBlock>
            }

            {this.renderDraftButton()}

            <LoaderButton
            block
            bsStyle="primary"
            bsSize="large"
            disabled={!this.validateForm()}
            type="submit"
            isLoading={this.state.isLoading}
            text="Create"
            loadingText="Creating..."
          />
          {this.state.rateLimit > 0 &&
          <HelpBlock>You are doing that too often, please wait {this.state.rateLimit} minutes to create a new issue.</HelpBlock>
          }
          </div>
        </form>
      </div>
    );
  }
}
